/**
 * Super Admin shell — uses the shared <AdminShell> for mobile-responsive
 * navigation (hamburger drawer below 1024px). Adds an extra mobile-number
 * gate so the route is impossible to reach with a non-canonical mobile,
 * even before the backend rejects the token.
 */
import { Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Building2, LayoutDashboard, Receipt, LifeBuoy, Tag, Settings, Flame, ShieldAlert, ShieldCheck, DatabaseBackup, SlidersHorizontal } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";

const SUPER_ADMIN_MOBILE = "9858558555";

export default function SuperLayout() {
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();

    const isAuthorized = !!user
        && user.role === "SUPER_ADMIN"
        && user.mobile_number === SUPER_ADMIN_MOBILE;

    useEffect(() => {
        if (loading) return;
        if (!user) {
            nav("/login", { state: { role: "SUPER_ADMIN" } });
            return;
        }
        if (!isAuthorized) {
            // The user IS logged in (as Tenant/Owner/Rider/Customer) but is
            // NOT the canonical super admin. Force-logout to nuke whatever
            // role they currently hold so they can't keep poking /super.
            toast.error("Super admin access is restricted.");
            logout();
            nav("/login", { state: { role: "SUPER_ADMIN" } });
        }
    }, [loading, user, isAuthorized, nav, logout]);

    if (!isAuthorized) return null;

    const items = [
        { to: "/super", icon: LayoutDashboard, label: "Overview", end: true },
        { to: "/super/tenants", icon: Building2, label: "Tenants" },
        { to: "/super/features", icon: SlidersHorizontal, label: "Features" },
        { to: "/super/plans", icon: Tag, label: "Plans" },
        { to: "/super/billing", icon: Receipt, label: "Billing" },
        { to: "/super/firebase", icon: Flame, label: "Firebase" },
        { to: "/super/settings", icon: Settings, label: "Settings" },
        { to: "/super/policies", icon: ShieldCheck, label: "Legal Policies" },
        { to: "/super/tickets", icon: LifeBuoy, label: "Support" },
        { to: "/super/backups", icon: DatabaseBackup, label: "Backups" },
        { to: "/super/maintenance", icon: ShieldAlert, label: "Maintenance" },
    ];

    return (
        <AdminShell
            items={items}
            user={user}
            title="Super Admin"
            testidPrefix="super-nav"
            onLogout={async () => { try { await logout(); } finally { nav("/login"); } }}
        >
            <Outlet />
        </AdminShell>
    );
}
